import { round } from 'lodash';
import { Metric } from '../types/metric.type';

const ALERTS_THRESHOLDS = {
  HR: {
    high: 100,
    low: 60,
    low_urgent: 30,
  },
  JTcF: {
    significant: 370,
    urgent: 400,
  },
  K: {
    high: 5.5,
    high_urgent: 7,
    low: 3.5,
    low_urgent: 3,
  },
  P: {
    high: 120,
  },
  PQ: {
    high: 200,
    low: 120,
  },
  P_axis: {
    high: 75,
    low: 20,
  },
  QRS: {
    high: 110,
  },
  QRS_axis: {
    high: 90,
    low: -30,
  },
  QTc: {
    F: {
      significant: 460,
    },
    M: {
      significant: 450,
    },
    urgent: 500,
    low_urgent: 340,
  },
  QTcF: {
    significant: 470,
    urgent: 500,
  },
  Smith: {
    high: 23.3,
  },
  Smith_4: {
    high: 18.2,
  },
};

export enum Alert {
  NORMAL,
  SIGNIFICANT,
  URGENT,
}

export const getAlertLevel = (
  metric: Metric,
  sex: string | undefined,
): number => {
  switch (metric.description) {
    case 'QTcF':
      if (metric.value > ALERTS_THRESHOLDS.QTcF.urgent) {
        return Alert.URGENT;
      } else if (metric.value >= ALERTS_THRESHOLDS.QTcF.significant) {
        return Alert.SIGNIFICANT;
      }
      return Alert.NORMAL;
    case 'QTcR':
    case 'QTcB': {
      const significantThreshold =
        sex === 'F'
          ? ALERTS_THRESHOLDS.QTc.F.significant
          : ALERTS_THRESHOLDS.QTc.M.significant;
      if (
        metric.value >= ALERTS_THRESHOLDS.QTc.urgent ||
        metric.value <= ALERTS_THRESHOLDS.QTc.low_urgent
      ) {
        return Alert.URGENT;
      } else if (metric.value >= significantThreshold) {
        return Alert.SIGNIFICANT;
      }
      return Alert.NORMAL;
    }
    case 'JTcF':
      if (metric.value > ALERTS_THRESHOLDS.JTcF.urgent) {
        return Alert.URGENT;
      } else if (metric.value >= ALERTS_THRESHOLDS.JTcF.significant) {
        return Alert.SIGNIFICANT;
      }
      return Alert.NORMAL;
    case 'HR':
      if (metric.value <= ALERTS_THRESHOLDS.HR.low_urgent) {
        return Alert.URGENT;
      } else if (
        metric.value >= ALERTS_THRESHOLDS.HR.high ||
        metric.value <= ALERTS_THRESHOLDS.HR.low
      ) {
        return Alert.SIGNIFICANT;
      }
      return Alert.NORMAL;
    case 'K':
      if (
        metric.value >= ALERTS_THRESHOLDS.K.high_urgent ||
        metric.value < ALERTS_THRESHOLDS.K.low_urgent
      ) {
        return Alert.URGENT;
      } else if (
        metric.value >= ALERTS_THRESHOLDS.K.high ||
        metric.value < ALERTS_THRESHOLDS.K.low
      ) {
        return Alert.SIGNIFICANT;
      }
      return Alert.NORMAL;
    case 'P':
      return metric.value >= ALERTS_THRESHOLDS.P.high
        ? Alert.SIGNIFICANT
        : Alert.NORMAL;
    case 'QRS axis':
      return metric.value <= ALERTS_THRESHOLDS.QRS_axis.low ||
        metric.value >= ALERTS_THRESHOLDS.QRS_axis.high
        ? Alert.SIGNIFICANT
        : Alert.NORMAL;
    case 'QRS':
      return metric.value >= ALERTS_THRESHOLDS.QRS.high
        ? Alert.SIGNIFICANT
        : Alert.NORMAL;
    case 'PQ':
      return metric.value < ALERTS_THRESHOLDS.PQ.low ||
        metric.value >= ALERTS_THRESHOLDS.PQ.high
        ? Alert.SIGNIFICANT
        : Alert.NORMAL;
    case 'min sinus PQ':
      return metric.value < ALERTS_THRESHOLDS.PQ.low ||
        metric.value >= ALERTS_THRESHOLDS.PQ.high
        ? Alert.SIGNIFICANT
        : Alert.NORMAL;
    case 'P axis':
      return metric.value <= ALERTS_THRESHOLDS.P_axis.low ||
        metric.value >= ALERTS_THRESHOLDS.P_axis.high
        ? Alert.SIGNIFICANT
        : Alert.NORMAL;
    case 'Smith':
      return metric.value > ALERTS_THRESHOLDS.Smith.high
        ? Alert.URGENT
        : Alert.NORMAL;
    case 'Smith_4': {
      // smith uses 1-digit precision, see front#932
      const value = round(metric.value, 1);
      return value > ALERTS_THRESHOLDS['Smith_4'].high
        ? Alert.URGENT
        : Alert.NORMAL;
    }
    default:
      return Alert.NORMAL;
  }
};
